import { useCallback, useEffect, useRef, useState } from 'react';

export interface SearchOptions<T> {
    debounceDelay?: number;
    loadingMode: 'debounce' | 'immediate';
    searchFn: (searchQuery: string) => Promise<T[]>;
    minSearchQueryLength?: number;
}

export const useSearch = <T>({
    debounceDelay,
    loadingMode,
    searchFn,
    minSearchQueryLength,
}: SearchOptions<T>) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<T[]>([]);
    const debounceTimeoutId = useRef<NodeJS.Timeout | null>(null);
    const activeSearchDate = useRef<number>(0);

    useEffect(() => {
        if (searchQuery.length < (minSearchQueryLength || 0)) {
            setLoading(false);
            setResults([]);
            return;
        }
        if (loadingMode === 'immediate') {
            setLoading(true);
        }

        debounceTimeoutId.current = setTimeout(async () => {
            if (loadingMode === 'debounce') {
                setLoading(true);
            }
            // This is required to cancel displaying results from a previous search
            const activeRef = Date.now() + Math.random();
            activeSearchDate.current = activeRef;

            const results = await searchFn(searchQuery);
            if (activeSearchDate.current !== activeRef) {
                return;
            }
            setResults(results);
            setLoading(false);
        }, debounceDelay);

        return () => {
            if (debounceTimeoutId.current) {
                clearTimeout(debounceTimeoutId.current);
                activeSearchDate.current = 0;
            }
        };
    }, [debounceDelay, loadingMode, minSearchQueryLength, searchFn, searchQuery]);

    const search = useCallback((searchQuery: string) => {
        setSearchQuery(searchQuery);
    }, []);

    return {
        search,
        results,
        loading,
        searchQuery,
    };
};
